import React, { useState, useEffect, useRef } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import "./App.css";
import { MdAccessTime } from "react-icons/md";

function App() {
  const [sleepTimerDuration, setSleepTimerDuration] = useState(1); //1 minute
  const adjustSleepTimer = () => {
    const newDuration = window.prompt(
      "Enter new sleep timer duration in minutes:"
    );
    if (newDuration) {
      setSleepTimerDuration(newDuration);
    }
  };

  const audioFiles = [];
  for (let i = 0; i < 1400; i++) {
    audioFiles.push(`/SocialBehavior/audio/s_${i}.wav`);
  }

  const [isSleepModeOn, setIsSleepModeOn] = useState(false);
  const toggleSleepMode = () => {
    setIsSleepModeOn(!isSleepModeOn);
    // You may want to consider removing or altering this line.
    // setSleepTimerDuration((prevDuration) => prevDuration + 10); // Increase the duration by 10 minutes each time
  };

  useEffect(() => {
    let sleepTimer;

    if (isSleepModeOn) {
      // If sleep mode is turned on, set up a timer to stop audio after 30 minutes.
      sleepTimer = setTimeout(() => {
        setIsPlaying(false);
      }, sleepTimerDuration * 60 * 1000); // Time is in milliseconds, so sleepTimerDuration min * 60 sec/min * 1000 ms/sec
    } else {
      // If sleep mode is turned off, clear the timer.
      if (sleepTimer) {
        clearTimeout(sleepTimer);
      }
    }

    // Clear timeout when the component is unmounted or when isSleepModeOn changes
    return () => {
      if (sleepTimer) {
        clearTimeout(sleepTimer);
      }
    };
  }, [isSleepModeOn]);

  const [sentences, setSentences] = useState([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [nextSentenceIndex, setNextSentenceIndex] = useState(
    currentAudioIndex + 1
  );
  const [prevSentenceIndex, setPrevSentenceIndex] = useState(
    currentAudioIndex - 1
  );

  useEffect(() => {
    setNextSentenceIndex(currentAudioIndex + 1);
    setPrevSentenceIndex(currentAudioIndex - 1);
  }, [currentAudioIndex]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [userPressedPlay, setUserPressedPlay] = useState(false);

  useEffect(() => {
    // Load current audio index from localStorage
    const savedAudioIndex = localStorage.getItem("currentAudioIndex");
    if (savedAudioIndex !== null) {
      setCurrentAudioIndex(parseInt(savedAudioIndex));
    }
  }, []);

  useEffect(() => {
    // Save current audio index to localStorage
    if (currentAudioIndex !== 0) {
      localStorage.setItem("currentAudioIndex", currentAudioIndex.toString());
    }
  }, [currentAudioIndex]);

  useEffect(() => {
    const currentSpeed = localStorage.getItem("currentSpeed");
    if (currentSpeed !== null) {
      setPlaybackRate(parseInt(currentSpeed));
    }
  }, []);

  useEffect(() => {
    if (playbackRate !== 1) {
      localStorage.setItem("currentSpeed", playbackRate.toString());
    }
  }, [playbackRate]);

  useEffect(() => {
    fetch(process.env.PUBLIC_URL + "/SocialBehavior/SocialBehavior.txt")
      .then((response) => response.text())
      .then((text) => {
        // Split text into sentences
        const loadedSentences = text.split(/(?<=\.)\s|\n/);
        setSentences(loadedSentences);
      });
  }, []);

  useEffect(() => {
    audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, playbackRate]);

  useEffect(() => {
    if (audioRef.current) {
      if (userPressedPlay) {
        audioRef.current.pause();
        audioRef.current.src =
          process.env.PUBLIC_URL + audioFiles[currentAudioIndex];
        setIsPlaying(true);
        audioRef.current.playbackRate = playbackRate;
        audioRef.current.play();
      }
    }
  }, [currentAudioIndex, playbackRate, userPressedPlay]);

  useEffect(() => {
    if (currentlyPlaying) {
      // Scroll current sentence into view if it is not visible
      const { top, bottom } = currentlyPlaying.getBoundingClientRect();
      if (top < 0 || bottom > window.innerHeight * 0.8) {
        currentlyPlaying.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }
  }, [currentlyPlaying]);

  const handleSentenceClick = (index) => {
    setCurrentAudioIndex(index);
  };

  const renderSentences = () => {
    return sentences.map((sentence, index) => {
      const isHighlighted = index === currentAudioIndex;
      const highlightClass = isHighlighted ? "highlight" : "unhighlight";
      if (sentence.trim().length === 0) {
        return (
          <div key={index} style={{ paddingTop: "12px", width: "100%" }}></div>
        );
      }
      return (
        <React.Fragment key={index}>
          <span
            className={`sentence ${highlightClass}`}
            ref={(ref) => {
              if (isHighlighted) {
                setCurrentlyPlaying(ref);
              }
            }}
            onClick={() => handleSentenceClick(index)}
          >
            {sentence.trim()}
          </span>
          {index < sentences.length - 1 && " "}
        </React.Fragment>
      );
    });
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentAudioIndex((prevIndex) => (prevIndex + 1) % audioFiles.length);
  };

  const togglePlay = () => {
    setUserPressedPlay((c) => {
      if (!c) {
        return true;
      }
    });
    setIsPlaying(!isPlaying);
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  const handleSpeedChange = () => {
    const playbackRates = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4];
    const currentIndex = playbackRates.indexOf(playbackRate);
    const newIndex = (currentIndex + 1) % playbackRates.length;
    const newRate = playbackRates[newIndex];
    setPlaybackRate(newRate);
  };

  return (
    <div className="flex flex-col h-screen">
      <header className="flex-none bg-black text-white py-4 px-6 flex justify-between items-center">
        <h1 className="text-lg font-bold">
          Social Behaviour: Genes, Ecology and Evolution
        </h1>
        <div className="flex items-center">
          {isSleepModeOn && (
            <button
              onClick={adjustSleepTimer}
              className="mr-2"
              style={{ color: "teal", background: "none", border: "none" }}
            >
              Sleep Timer: {sleepTimerDuration} min
            </button>
          )}
          <button className="ml-4" onClick={toggleSleepMode}>
            <MdAccessTime color={isSleepModeOn ? "teal" : "white"} size={30} />
          </button>
        </div>
      </header>

      <div className="flex-grow overflow-y-scroll px-6 container mx-auto my-8 xl:my-18">
        <audio
          ref={audioRef}
          src={process.env.PUBLIC_URL + audioFiles[currentAudioIndex]}
          onEnded={handleAudioEnd}
        />
        {renderSentences()}
      </div>

      <footer className="flex-none bg-black text-white py-4 px-6 flex justify-center">
        <div>
          <button
            className="bg-black hover:bg-grey-300 text-white font-bold py-2 px-4 mx-2 rounded"
            onClick={() => setCurrentAudioIndex(prevSentenceIndex)}
            disabled={prevSentenceIndex < 0}
          >
            Prev
          </button>
          <button
            className="bg-black hover:bg-grey-300 text-white font-bold py-2 px-4 mx-2 rounded"
            onClick={togglePlay}
          >
            {isPlaying ? (
              <FaPause className="text-white text-md" />
            ) : (
              <FaPlay className="text-white text-md" />
            )}
          </button>
          <button
            className="bg-black hover:bg-grey-300 text-white font-bold py-2 px-4 mx-2 rounded"
            onClick={handleSpeedChange}
          >
            {playbackRate}x
          </button>
          <button
            className="bg-black hover:bg-grey-300 text-white font-bold py-2 px-4 mx-2 rounded"
            onClick={() => setCurrentAudioIndex(nextSentenceIndex)}
            disabled={nextSentenceIndex >= sentences.length}
          >
            Next
          </button>
        </div>
      </footer>
    </div>
  );
}

export default App;
